import { useLocation, Outlet, Navigate } from "react-router-dom";
import Cookies from "js-cookie";
import useAuth from "../hook/useAuth";
import useAccessControl from "../hook/useAccessControl";
import { isTokenExpired } from "../utils/isTokenExpired";
import { appAdminRoutes } from "../routes/adminRoutes";
import { appAdminSettingRoutes } from "../routes/adminSettingsRoutes";

type Props = {
    allowedRoles: string[];
};

const RequiredAuth = ({ allowedRoles }: Props) => {
    const location = useLocation();
    const { setAuth }: any = useAuth();
    const { permissions, loading }: any = useAccessControl();

    const token = Cookies.get("token-express-tire");
    const role = Cookies.get("role-express-tire") || "";
    const expires = Cookies.get("expires-express-tire");

    if (!token) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (isTokenExpired(Number(expires))) {
        setAuth(null);

        Cookies.remove("token-express-tire");
        Cookies.remove("role-express-tire");
        Cookies.remove("userId-express-tire");
        Cookies.remove("roleId-express-tire");
        Cookies.remove("profileImg-express-tire");
        Cookies.remove("expires-express-tire");

        return <Navigate to="/login" replace />;
    }

    const isRoleAllowed = allowedRoles?.some(
        (r) => r.toLowerCase() === role.toLowerCase()
    );

    if (isRoleAllowed) {
        return <Outlet />;
    }

    if (loading) {
        return <div className="p-10 text-center">Loading...</div>;
    }

    const permissionValues = Object.values(permissions || {});
    const noPermissions =
        permissionValues.length === 0 ||
        permissionValues.every((value) => value === false);

    if (noPermissions) {
        return <Navigate to="/unauthorized" replace />;
    }

    const currentPath = location.pathname;
    const flattenRoutes = (routes: any[]) => {
        const result: any[] = [];

        routes.forEach(route => {
            if (route.child) {
                route.child.forEach((child: any) => {
                    result.push(child);
                });
            }
            result.push(route);
        });

        return result;
    };

    const allRoutes = flattenRoutes([
        ...appAdminRoutes,
        ...appAdminSettingRoutes
    ]);

    const matchedRoute = allRoutes.find((route: any) =>
        currentPath.startsWith(route.path)
    );

    if (!matchedRoute) {
        return <Navigate to="/unauthorized" replace />;
    }

    const normalizedSection = matchedRoute.section
        .replace(/\s+/g, "")
        .replace(/-/g, "")
        .toLowerCase();

    const hasPermission = permissions?.[normalizedSection] === true;

    return hasPermission ? (
        <Outlet />
    ) : (
        <Navigate to="/unauthorized" replace />
    );
};

export default RequiredAuth;